/**
 * Bottom-right floating-element stack orchestrator.
 *
 * Replaces the ad-hoc `glpflo:fab-state` + `glpflo:cookie-banner-state`
 * custom events that the chat widget used to listen to. Any floating
 * element that wants to participate calls `useBottomRightStack({ id,
 * priority, visible, height })` — it both registers itself in the stack
 * (so higher-priority elements can clear it) AND receives its own
 * computed `bottom` px so it can offset itself out of the way of lower-
 * priority neighbours.
 *
 * Priority convention (low → high = closer to viewport bottom):
 *     10  Cookie banner             (full-width on mobile)
 *     20  Compare-Plans FAB         (right-corner pill)
 *     30  Patient chat bubble       (right-corner primary action)
 *     40  Patient help guide button (right-corner secondary action)
 *
 * A new floating CTA just picks an unused priority and plugs in — no
 * geometry math required at the call site.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const StackCtx = createContext(null);

// Read once at module load. The dev overlay is purely opt-in; visitors
// who don't append `?debug=stack` to the URL pay zero bytes (the
// DebugOverlay just renders null). Re-checked each render so toggling
// the query param at runtime works during a single SPA session.
function isDebugEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("debug") === "stack";
  } catch {
    return false;
  }
}

/**
 * Dev-only floating panel that lists every registered stack item with
 * its computed bottom px. Gated behind `?debug=stack` so production
 * visitors never see it. Renders at top-left so it doesn't overlap the
 * stack it's debugging.
 */
function StackDebugOverlay() {
  const ctx = useContext(StackCtx);
  if (!ctx || !isDebugEnabled()) return null;
  const { items, baseInset, gap } = ctx;
  const rows = Object.entries(items)
    .map(([id, item]) => {
      // Replicate the same math the hook uses so the displayed value
      // exactly matches what each consumer actually applies.
      let bottom = baseInset;
      for (const [otherId, other] of Object.entries(items)) {
        if (otherId === id || !other.visible) continue;
        if (other.priority < item.priority) bottom += other.height + gap;
      }
      return { id, ...item, bottom };
    })
    .sort((a, b) => a.priority - b.priority);
  return (
    <div
      style={{
        position: "fixed", top: 8, left: 8, zIndex: 99999,
        background: "rgba(15,23,42,0.92)", color: "#fff",
        fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11,
        padding: "8px 10px", borderRadius: 8, lineHeight: 1.4,
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)", maxWidth: 360,
      }}
      data-testid="bottom-right-stack-debug"
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        BottomRightStack ({rows.length})
      </div>
      <div style={{ opacity: 0.6, marginBottom: 6 }}>
        base={baseInset}px · gap={gap}px
      </div>
      {rows.length === 0 ? (
        <div style={{ opacity: 0.6 }}>(empty)</div>
      ) : (
        <table style={{ borderCollapse: "collapse" }}>
          <thead style={{ opacity: 0.7 }}>
            <tr>
              <th style={{ textAlign: "left", paddingRight: 8 }}>pri</th>
              <th style={{ textAlign: "left", paddingRight: 8 }}>id</th>
              <th style={{ textAlign: "right", paddingRight: 8 }}>h</th>
              <th style={{ textAlign: "right", paddingRight: 8 }}>vis</th>
              <th style={{ textAlign: "right" }}>↑bottom</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ opacity: r.visible ? 1 : 0.4 }}>
                <td style={{ paddingRight: 8 }}>{r.priority}</td>
                <td style={{ paddingRight: 8 }}>{r.id}</td>
                <td style={{ textAlign: "right", paddingRight: 8 }}>{r.height}</td>
                <td style={{ textAlign: "right", paddingRight: 8 }}>{r.visible ? "✓" : "·"}</td>
                <td style={{ textAlign: "right", color: "#a78bfa", fontWeight: 600 }}>{r.bottom}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function BottomRightStackProvider({ children, baseInset = 24, gap = 16 }) {
  // Map<id, { priority, visible, height }>. Plain object so React's shallow
  // equality on the context value works predictably with the memo below.
  const [items, setItems] = useState({});

  const register = useCallback((id, item) => {
    setItems((prev) => {
      if (item === null) {
        if (!(id in prev)) return prev;
        // eslint-disable-next-line no-unused-vars
        const { [id]: _drop, ...rest } = prev;
        return rest;
      }
      const existing = prev[id];
      if (existing
          && existing.priority === item.priority
          && existing.visible === item.visible
          && existing.height === item.height) {
        return prev; // no-op — prevents render loops
      }
      return { ...prev, [id]: item };
    });
  }, []);

  const value = useMemo(
    () => ({ items, register, baseInset, gap }),
    [items, register, baseInset, gap],
  );
  return (
    <StackCtx.Provider value={value}>
      {children}
      <StackDebugOverlay />
    </StackCtx.Provider>
  );
}

/**
 * Register this element in the stack and return its computed `bottom` (px).
 *
 * If the provider isn't mounted (e.g. in a Jest unit test or an island
 * that lives outside the main app tree) the hook degrades gracefully —
 * `bottom` defaults to the would-be base inset (24px) so the element
 * still renders in a sensible spot.
 */
export function useBottomRightStack({ id, priority, visible, height }) {
  const ctx = useContext(StackCtx);
  // Stash the live ctx in a ref so the registration effect below does NOT
  // depend on `ctx` (which is a fresh memo object on every items-change).
  // Without this, the effect's cleanup would unregister the item, which
  // mutated `items` → produced a new `ctx` → re-ran the effect → re-added
  // the item → mutated `items` → … infinite "Maximum update depth"
  // render loop that froze the page's click handlers.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  useEffect(() => {
    const c = ctxRef.current;
    if (!c) return undefined;
    c.register(id, { priority, visible: !!visible, height: height || 0 });
    return () => { ctxRef.current?.register(id, null); };
  }, [id, priority, visible, height]);

  if (!ctx) return 24;
  let bottom = ctx.baseInset;
  for (const [otherId, item] of Object.entries(ctx.items)) {
    if (otherId === id || !item.visible) continue;
    if (item.priority < priority) bottom += item.height + ctx.gap;
  }
  return bottom;
}
